import { PublicClientApplication } from '@azure/msal-browser';
import axios from 'axios';
import { getMotorApiToken } from './motorApiService';

const MOTOR_API_DOMAIN = process.env.REACT_APP_MOTOR_API_DOMAIN || 'https://motor-uat-motor-api.dchmsc.com';

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_MSAL_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_MSAL_TENANT_ID}`,
    redirectUri: window.location.origin + '/',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Ensure MSAL is initialized and redirect handled
let msalReady = false;
const ensureMsalReady = async () => {
  if (!msalReady) {
    await msalInstance.initialize();
    // Handle redirect response if returning from Microsoft login
    try {
      const redirectResponse = await msalInstance.handleRedirectPromise();
      if (redirectResponse) {
        window.__msalRedirectResponse = redirectResponse;
      }
    } catch (err) {
      console.error('SSO: Error handling redirect:', err);
    }
    msalReady = true;
  }
};

// Initialize immediately
ensureMsalReady();

/**
 * Parse user name from MSAL account
 */
const getUserName = (account) => {
  if (account?.name) return account.name;
  if (account?.idTokenClaims?.name) return account.idTokenClaims.name;
  if (account?.username) return account.username.split('@')[0];
  return 'User';
};

export const authService = {
  /**
   * Microsoft SSO Login
   */
  login: async () => {
    await ensureMsalReady();

    let loginResponse = null;

    // Check if we have a redirect response waiting
    if (window.__msalRedirectResponse) {
      loginResponse = window.__msalRedirectResponse;
      window.__msalRedirectResponse = null;
    }

    // Try silent login if no redirect response
    if (!loginResponse) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        try {
          loginResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
        } catch (silentError) {
          console.log('SSO: Silent login not available, redirecting...');
        }
      }
    }

    // If still no response, redirect to Microsoft login
    if (!loginResponse) {
      await msalInstance.loginRedirect(loginRequest);
      return null;
    }

    // Validate idToken with Motor API
    const motorToken = await getMotorApiToken();

    const ssoResponse = await axios.post(
      `${MOTOR_API_DOMAIN}/pmm-api/login-sso`,
      {
        token: motorToken,
        app_id: 45,
        id_token: loginResponse.idToken,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const result = ssoResponse.data?.result;
    const statusObj = result?.[0]?.[0];
    const errType = ssoResponse.data?.err_type;

    if (errType !== 0 || statusObj?.Status !== 0) {
      throw new Error(statusObj?.Message || 'SSO validation failed');
    }

    const sessionInfo = result?.[1]?.[0] || {};
    const userName = getUserName(loginResponse.account);

    const user = {
      id: sessionInfo.pmm_user_id || loginResponse.account?.localAccountId,
      email: loginResponse.account?.username,
      name: userName,
      role: 'User',
      sessionToken: sessionInfo.user_session_token,
    };

    const appToken = sessionInfo.user_session_token || loginResponse.idToken;

    console.log('SSO: Login complete. User:', user.name);

    return { user, token: appToken };
  },

  /**
   * Verify stored token
   */
  verifyToken: async (token) => {
    await ensureMsalReady();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && token) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          return { valid: true, user: JSON.parse(storedUser) };
        } catch (e) { /* fall through */ }
      }

      const account = accounts[0];
      return {
        valid: true,
        user: {
          id: account.localAccountId,
          email: account.username,
          name: getUserName(account),
          role: 'User',
        },
      };
    }

    throw new Error('No valid session');
  },

  /**
   * Logout
   */
  logout: async () => {
    await ensureMsalReady();
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    try {
      await msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin + '/login' });
    } catch (e) {
      console.log('SSO: Logout error, clearing local state:', e.message);
    }
  },
};
